import { ICarrierListSupportedResponse } from '@geartrack/shared-lib/dist/tracker-api/httpResponsesTypes'
import PackageModel from '@geartrack/shared-lib/dist/PackageModel'
import { useEffect, useState } from 'react'
import { fetchPackageInformation, getRequiredParams } from '../libraries/tracker.api'
import { notifyAndLog } from '../libraries/utils'
import { IOrderResponse } from '../libraries/interfaces/tracker.interfaces'
import { useSettingsStore } from '../state/settings.store'
import { getLatestDateFromOrder } from '../pages/PackagesPage/PackageTimelines/order-packages'
import moment from 'moment'
import usePackagesRepository from './usePackagesRepository'

const useFetchTrackingInfo = (pkg: PackageModel, lang: string, carriersListResponse: ICarrierListSupportedResponse) => {
  const [responses, setResponses] = useState<IOrderResponse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const pkgRepo = usePackagesRepository()
  const packagesSortByLatestStateDate = useSettingsStore(s => s.packagesSortByLatestStateDate)

  useEffect(() => {
    // track if this component is still mounted, if not, discard promises results
    let isSubscribed = true

    // in the case useEffect runs multiple times, we need to reset the current data
    setIsLoading(true)
    setResponses([])

    for (let carrier of carriersListResponse.carriers) {
      // Add packages information as soon as we get a response
      fetchPackageInformation(
        carrier.name,
        carriersListResponse.id?.trim(),
        getRequiredParams(pkg.extraData, carrier.necessaryParams),
        lang
      )
        // eslint-disable-next-line no-loop-func
        .then(resp => {
          if (isSubscribed) {
            setResponses(prev => [...prev, resp])
          }
        })
        // eslint-disable-next-line no-loop-func
        .catch(e => {
          if (isSubscribed) {
            setResponses(prev => [...prev, null])
            notifyAndLog(e, {
              carrier: carrier.name,
              trackingNumber: carriersListResponse.id?.trim(),
              pkg: pkg,
              config: e.config,
              request: e.request,
              response: e.response,
            })
          }
        })
    }

    return () => {
      isSubscribed = false
    }
    // eslint-disable-next-line
  }, [pkg.extraData, lang, carriersListResponse])

  // all requests done, set loading as false
  useEffect(() => {
    if (responses.length === carriersListResponse.carriers.length && isLoading) {
      setIsLoading(false)
    }
  }, [carriersListResponse.carriers.length, isLoading, responses.length])

  useEffect(() => {
    if (packagesSortByLatestStateDate) {
      const latestDate = getLatestStateDateFromResponsesList(responses)
      if (latestDate) {
        pkgRepo.addSortFieldLastStateDate(pkg, latestDate)
      }
    }
  }, [responses, packagesSortByLatestStateDate, pkg, pkgRepo])

  return {
    isLoading,
    responses,
  }
}

function getLatestStateDateFromResponsesList(responses: IOrderResponse[]): number | null {
  const datesInMs = responses
    .map(getLatestDateFromOrder)
    .filter(s => s)
    .map(dateString => moment(dateString).valueOf())

  if (datesInMs.length === 0) {
    return null
  }

  return datesInMs.sort((a, b) => b - a)[0]
}

export default useFetchTrackingInfo
